// Game regions (DR-0050; Theo, 28 Sep 2026: "We need servers us, EU, sea").
//
// The same game server image runs in three regions. US East is the HOME server:
// social (friends, parties, chat), the hub and Ludus live there and only there,
// one global social space. A game's own rooms (Firestrike's lobby and its run)
// can live on any region's server; the player picks one, or "auto" picks the
// lowest measured round trip (regionProbe.ts). Co-op is client-host, so a lobby
// and its run stay on ONE server that every player in it connects to, even
// players on other continents.
//
// This file is the region table and its pure rules (unit-tested).

import { resolveColyseusEndpoint } from "./networkConfig";
import type { RegionInfo } from "./serverRegion";

export const GAME_REGION_IDS = ["us", "eu", "sea"] as const;
export type GameRegionId = (typeof GAME_REGION_IDS)[number];

/** The player's setting: one region, or "auto" (lowest measured round trip). */
export const GAME_REGION_CHOICES = ["auto", ...GAME_REGION_IDS] as const;
export type GameRegionChoice = (typeof GAME_REGION_CHOICES)[number];

/** The home server's region, and auto's fallback when no region answers. */
export const HOME_GAME_REGION: GameRegionId = "us";

// Production endpoints of the regional servers (Railway services running the
// same image as the home server). "" = not deployed yet: the region is hidden
// everywhere and auto never picks it. US East needs no constant: it is the home
// server, resolveColyseusEndpoint() (https://server.actuniverse.com).
// Railway services game-server-eu (europe-west4) and game-server-sea
// (asia-southeast1), live since 28 Sep 2026. DNS-only CNAMEs in Theo's
// Cloudflare, like server.actuniverse.com, so a host move needs no rebuild.
export const EU_ENDPOINT = "https://server-eu.actuniverse.com";
export const SEA_ENDPOINT = "https://server-sea.actuniverse.com";

export type GameRegion = RegionInfo & {
  id: GameRegionId;
  /** Colyseus endpoint (http/https, no trailing slash); "" = unavailable. */
  endpoint: string;
};

const TABLE: ReadonlyArray<RegionInfo & { id: GameRegionId }> = [
  { id: "us", short: "US EAST", name: "US East", place: "Virginia" },
  { id: "eu", short: "EU WEST", name: "EU West", place: "Netherlands" },
  { id: "sea", short: "ASIA SE", name: "Southeast Asia", place: "Singapore" },
];

export function isGameRegionId(value: unknown): value is GameRegionId {
  return typeof value === "string" && (GAME_REGION_IDS as readonly string[]).includes(value);
}

/** Region id → endpoint, from VITE_GAME_REGIONS ("" hides a region). */
export type RegionEndpointOverride = Partial<Record<GameRegionId, string>>;

/** Parse a VITE_GAME_REGIONS value, e.g. `{"us":"http://localhost:2567","eu":"http://localhost:2568","sea":""}`.
 *  Unknown keys and non-string values are ignored; anything that isn't a JSON
 *  object means no override. */
export function parseRegionOverride(raw: string | null | undefined): RegionEndpointOverride | null {
  if (!raw || !raw.trim()) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const override: RegionEndpointOverride = {};
  for (const id of GAME_REGION_IDS) {
    const value = (parsed as Record<string, unknown>)[id];
    if (typeof value === "string") override[id] = value.trim();
  }
  return override;
}

/**
 * The region table with this build's endpoints. An override entry wins.
 * Otherwise production uses the home server for US East and the constants
 * above for the rest, and dev points every region at the home (local) server,
 * so every region is playable on one local server.
 */
export function buildGameRegions(options: {
  home: string;
  production: boolean;
  override?: RegionEndpointOverride | null;
}): GameRegion[] {
  const production: Record<GameRegionId, string> = { us: options.home, eu: EU_ENDPOINT, sea: SEA_ENDPOINT };
  return TABLE.map((region) => {
    const endpoint = options.override?.[region.id] ?? (options.production ? production[region.id] : options.home);
    return { ...region, endpoint: endpoint.trim().replace(/\/+$/, "") };
  });
}

let resolved: GameRegion[] | null = null;

/** This build's regions, all three in table order (endpoint "" = unavailable). */
export function gameRegions(): readonly GameRegion[] {
  resolved ??= buildGameRegions({
    home: resolveColyseusEndpoint(),
    production: import.meta.env.PROD,
    override: parseRegionOverride(import.meta.env.VITE_GAME_REGIONS as string | undefined),
  });
  return resolved;
}

/** The regions a player can use (non-empty endpoint), in table order. */
export function availableGameRegions(regions: readonly GameRegion[] = gameRegions()): GameRegion[] {
  return regions.filter((region) => region.endpoint !== "");
}

/** An available region by id; null for unknown or unavailable ids. */
export function gameRegionById(id: string | null | undefined, regions: readonly GameRegion[] = gameRegions()): GameRegion | null {
  return regions.find((region) => region.id === id && region.endpoint !== "") ?? null;
}

/** Measured round trips in ms per region; null or missing = no answer (yet). */
export type RegionRtts = Readonly<Partial<Record<GameRegionId, number | null>>>;

/**
 * Auto: the available region with the lowest measured round trip (ties go to
 * table order). With no answers at all, the home region when it's available,
 * else the first available one; null only when no region is available.
 */
export function pickAutoRegion(regions: readonly GameRegion[], rtts: RegionRtts): GameRegion | null {
  const available = availableGameRegions(regions);
  let best: GameRegion | null = null;
  let bestMs = Number.POSITIVE_INFINITY;
  for (const region of available) {
    const ms = rtts[region.id];
    if (typeof ms === "number" && Number.isFinite(ms) && ms < bestMs) {
      best = region;
      bestMs = ms;
    }
  }
  return best ?? available.find((region) => region.id === HOME_GAME_REGION) ?? available[0] ?? null;
}

/** The choice as it applies in this build: a region that isn't available here
 *  (e.g. saved on another build) counts as auto. */
export function effectiveRegionChoice(choice: GameRegionChoice, regions: readonly GameRegion[]): GameRegionChoice {
  return choice === "auto" || gameRegionById(choice, regions) ? choice : "auto";
}

/** Where a choice sends the player right now. */
export function resolveRegionChoice(choice: GameRegionChoice, regions: readonly GameRegion[], rtts: RegionRtts): GameRegion | null {
  const effective = effectiveRegionChoice(choice, regions);
  return effective === "auto" ? pickAutoRegion(regions, rtts) : gameRegionById(effective, regions);
}

/** The setting after one tap on the selector: auto → each available region → auto. */
export function nextRegionChoice(choice: GameRegionChoice, regions: readonly GameRegion[]): GameRegionChoice {
  const cycle: GameRegionChoice[] = ["auto", ...availableGameRegions(regions).map((region) => region.id)];
  const at = cycle.indexOf(effectiveRegionChoice(choice, regions));
  return cycle[(at + 1) % cycle.length]!;
}

/**
 * The selector's value text: "AUTO · EU WEST 24 ms" (auto and where it lands),
 * "EU WEST · 24 ms" (a fixed pick), "… · MEASURING" while the probe runs, and
 * "NO SIGNAL" for a fixed pick that didn't answer.
 */
export function regionChoiceText(
  choice: GameRegionChoice,
  regions: readonly GameRegion[],
  rtts: RegionRtts,
  measuring: boolean,
): string {
  const effective = effectiveRegionChoice(choice, regions);
  const region = resolveRegionChoice(effective, regions, rtts);
  if (!region) return effective === "auto" ? "AUTO" : "OFFLINE";
  const ms = rtts[region.id];
  const measured = typeof ms === "number" && Number.isFinite(ms) ? `${Math.round(ms)} ms` : null;
  if (effective === "auto") {
    if (measuring) return "AUTO · MEASURING";
    return measured ? `AUTO · ${region.short} ${measured}` : `AUTO · ${region.short}`;
  }
  if (measured) return `${region.short} · ${measured}`;
  return measuring ? `${region.short} · MEASURING` : `${region.short} · NO SIGNAL`;
}

/** One row of the top-right region picker (the server readout's menu). */
export type RegionMenuRow = {
  choice: GameRegionChoice;
  /** "AUTO", or the region's short name ("EU WEST"). */
  label: string;
  /** Right-hand text: where auto lands ("EU WEST 24 ms"), a round trip
   *  ("24 ms"), "…" / "MEASURING" while the probe runs, or "NO SIGNAL". */
  detail: string;
  /** The round trip behind `detail`, for its colour; null when there is none. */
  ms: number | null;
  checked: boolean;
};

/** The picker's rows: auto (and where it lands now), then every available
 *  region, with the player's current choice checked. */
export function regionMenuRows(
  choice: GameRegionChoice,
  regions: readonly GameRegion[],
  rtts: RegionRtts,
  measuring: boolean,
): RegionMenuRow[] {
  const effective = effectiveRegionChoice(choice, regions);
  const msOf = (id: GameRegionId): number | null => {
    const ms = rtts[id];
    return typeof ms === "number" && Number.isFinite(ms) ? Math.round(ms) : null;
  };
  const auto = pickAutoRegion(regions, rtts);
  const autoMs = auto ? msOf(auto.id) : null;
  const rows: RegionMenuRow[] = [
    {
      choice: "auto",
      label: "AUTO",
      detail: !auto ? "OFFLINE" : autoMs !== null ? `${auto.short} ${autoMs} ms` : measuring ? "MEASURING" : "NO SIGNAL",
      ms: autoMs,
      checked: effective === "auto",
    },
  ];
  for (const region of availableGameRegions(regions)) {
    const ms = msOf(region.id);
    rows.push({
      choice: region.id,
      label: region.short,
      detail: ms !== null ? `${ms} ms` : measuring ? "…" : "NO SIGNAL",
      ms,
      checked: effective === region.id,
    });
  }
  return rows;
}
